/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useMemo } from 'react';
import { Button, Stack } from '@mui/material';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import { informationSteps } from 'libs/mindUtils';
import { useAppDispatch } from 'store';
import { closeDialog } from 'store/dialog/dialogSlice';
import isEmpty from 'lodash/isEmpty';
import { showMessage } from 'store/message/messageSlice';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import {
  postGuideSave,
  postProjectSave,
  postProjectSurvey,
  postProjectSurveyAdditionSave,
  postProjectUserUpload,
  putProjectSave,
} from 'store/project/projectSlice';
import { IStepper4Props } from 'interface/project/projectInfo';
import { useNavigate } from 'react-router-dom';

function FooterButton(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { inputs, onChange } = useProjectInfoProvider();
  const { activeStep, completed, isStep1Valid, stepper4, stepper2, stepper3 } = inputs;

  const totalSteps = () => informationSteps.length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const completedSteps = () => Object.keys(completed).length;
  const allStepsCompleted = () => completedSteps() === totalSteps();
  const handleContinue = () => dispatch(closeDialog());

  const handleBack = async () => {
    if (activeStep - 1 === 1) {
      await handleStepper3Action(1);
    } else {
      onChange('activeStep', activeStep - 1);
    }
  };
  const handleNext = async () => {
    const isFinish = allStepsCompleted();
    const newActiveStep =
      isLastStep() && !isFinish ? informationSteps.findIndex((step, i) => !(i in completed)) : activeStep + 1;
    if (newActiveStep === 1 && isEmpty(inputs.projectId)) {
      await handleProjectSaveAction();
    } else if (newActiveStep === 1) {
      await handleProjectUpdateAction();
    } else if (newActiveStep === 2 && isEmpty(inputs.stepper2.id)) {
      dispatch(showMessage({ message: '진단도구를  선택해주세요.', variant: 'warning' }));
    } else if (newActiveStep === 2) {
      await handleStepper2Action();
    } else if (newActiveStep === 3) {
      await handleStepper3Action(newActiveStep);
    } else if (newActiveStep === 4) {
      await handleStepper4Action(newActiveStep);
    } else if (isLastStep()) {
      navigate('/admin/project/overview');
    } else {
      onChange('activeStep', newActiveStep);
      handleContinue();
    }
  };

  const handleProjectSaveAction = () => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectSave({
        projectType: inputs.projectType,
        projectName: inputs.projectName,
        projectStartAt: (inputs.projectStartAt as any)?.format('YYYYMMDD'),
        projectEndAt: (inputs.projectEndAt as any).format('YYYYMMDD'),
        compName: inputs.compName,
        compAdminList: inputs.compAdminList.map(obj => obj.id),
      }),
    )
      .unwrap()
      .then(response => {
        const { status, data } = response;
        if (status === 200) {
          onChange('projectId', data.result);
          onChange('activeStep', 1);
          if (inputs.userExcel.file != null && inputs.userExcel.rowCount > 0 && inputs.userExcel.action) {
            handleProjectUserUploadAction(data.result);
          }
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleProjectUserUploadAction = (_projectId: string) => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectUserUpload({
        projectId: _projectId,
        file: inputs.userExcel?.file,
        action: inputs.userExcel?.action || 'new',
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          console.log('userExcelResult', response);
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleProjectUpdateAction = async () => {
    dispatch(openBackdrop({}));
    await dispatch(
      putProjectSave({
        projectType: inputs.projectType,
        projectName: inputs.projectName,
        projectStartAt: (inputs.projectStartAt as any)?.format('YYYYMMDD'),
        projectEndAt: (inputs.projectEndAt as any).format('YYYYMMDD'),
        compName: inputs.compName,
        compAdminList: inputs.compAdminList.map(obj => obj.id),
        projectId: inputs.projectId,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          onChange('activeStep', 1);
          if (inputs.userExcel.file != null && inputs.userExcel.rowCount > 0 && inputs.userExcel.action) {
            handleProjectUserUploadAction(inputs.projectId);
          }
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleStepper2Action = () => {
    if (!isEmpty(inputs.stepper2.serverId)) {
      onChange('activeStep', 2);
      handleContinue();
      return;
    }
    dispatch(openBackdrop({}));
    dispatch(
      postProjectSurvey({
        projectId: inputs.projectId,
        surveyId: inputs.stepper2.id,
      }),
    )
      .unwrap()
      .then(response => {
        const { status, data } = response;
        if (status === 200) {
          onChange('activeStep', 2);
          handleContinue();
        } else if (status === 400) {
          // TODO: 진단 도구 설정 수정하는 API 유무에 따라 로직 변경필요(일단 진행해야하니 해당코드로 진행)
          // onChange('activeStep', 2);
          // handleContinue();
          dispatch(showMessage({ message: data?.message || '실패하였습니다.', variant: 'warning' }));
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleStepper3Action = (step: number) => {
    dispatch(openBackdrop({}));
    dispatch(
      postProjectSurveyAdditionSave({
        projectId: inputs.projectId,
        questions: inputs.stepper3.noAdditional ? [] : inputs.stepper3.questions,
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        console.log('response', response);
        if (status === 200) {
          onChange('activeStep', step);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const handleStepper4Action = (step: number) => {
    dispatch(openBackdrop({}));
    dispatch(
      postGuideSave({
        projectId: inputs.projectId,
        des: inputs.stepper4.projectDescription || '',
        agreement: inputs.stepper4.projectAgreement || '',
        complete: inputs.stepper4.completeMessage || '',
        emailTitle: inputs.stepper4.emailTitle || '',
        emailBody: inputs.stepper4.emailBody || '',
      }),
    )
      .unwrap()
      .then(response => {
        const { status } = response;
        if (status === 200) {
          onChange('activeStep', step);
          handleContinue();
        }
      })
      .catch(e => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  };

  const isStep4Valid = useMemo((): boolean => {
    const requiredKeys: (keyof IStepper4Props)[] = ['projectDescription', 'projectAgreement', 'completeMessage'];
    return requiredKeys.every(key => {
      const tmpCheck = stepper4[key];
      return !isEmpty(tmpCheck);
    });
  }, [stepper4]);

  const isButtonDisabled = useMemo((): boolean => {
    if (activeStep === 0 && !isStep1Valid) {
      return true;
    }
    if (activeStep === 1 && isEmpty(stepper2.id)) {
      return true;
    }
    if (activeStep === 2 && !stepper3.noAdditional && stepper3.questions.length === 0) {
      return true;
    }
    if (activeStep === 3 && !isStep4Valid) {
      return true;
    }
    return false;
  }, [activeStep, isStep1Valid, isStep4Valid, stepper2.id, stepper3.noAdditional, stepper3.questions.length]);

  return (
    <Stack
      direction="row"
      spacing={4}
      justifyContent="flex-end"
      sx={{
        display: 'flex',
        position: 'absolute',
        bottom: '12px',
        right: '24px',
        height: 40,
      }}
    >
      <Button
        disabled={activeStep === 0}
        onClick={handleBack}
        sx={theme => ({
          width: 100,
          backgroundColor: 'mind.grey.A0',
          color: 'text.primary',
          border: `1px solid ${theme.palette.mind.grey.disabled}`,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: 'mind.custom.greyHover',
          },
          ':disabled': {
            color: 'rgba(0, 0, 0, 0.26)',
            boxShadow: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            border: 'none',
          },
        })}
      >
        이전
      </Button>
      <Button color="primary" variant="contained" onClick={handleNext} sx={{ width: 100 }} disabled={isButtonDisabled}>
        {activeStep === 4 ? '생성완료' : '다음'}
      </Button>
    </Stack>
  );
}

export default memo(FooterButton);
